import { useCloseMultipleFlags, useGetUserFlagsDetails, useGetUserProfile } from '@actions';
import FinishFlagIcon from '@assets/icons/Icon-awesome-flag-checkered.svg';
import { ReactComponent as BlockIcon } from '@assets/icons/V4/block-icon.svg';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import Table from '@components/V4/Table/Table';
import TriStateToggle from '@components/V4/TriStateToggle/TriStateToggle';
import { useAuthState } from '@contexts/AuthContext';
import DetailsModal, {
  DetailsModalPage,
} from '@modules/Admin/components/DetailsModal/DetailsModal';
import { MentorDetailsPageContext } from '@modules/Admin/components/DetailsModal/MentorDetailsPage/MentorDetailsPage';
import { StudentDetailsPageContext } from '@modules/Admin/components/DetailsModal/StudentDetailsPage/StudentDetailsPage';
import { UsersWithFlagsDTO } from '@shared/clientTypes/DTOs/FlagsDTO';
import { TriState } from '@shared/clientTypes/misc';
import { FLAG_STATUS, FLAG_TYPES } from '@shared/constants';
import { FlagStatus, UserRole, FlagTypes } from '@shared/enums';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import useToast from 'apps/agora/src/hooks/useToast';
import EmptyStateCard from 'apps/agora/src/views/UserView/components/EmptyStateCard';
import { useState, useEffect } from 'react';
import MentorDetailsSection from '../../../components/DetailsModal/MentorDetailsPage/MentorDetailsSection/MentorDetailsSection';
import StudentDetailsSection from '../../../components/DetailsModal/StudentDetailsPage/StudentDetailsSection/StudentDetailsSection';

type UserWithFlagsData = UsersWithFlagsDTO['users'][0];
type FlagDetailsData = {
  _id: string;
  type: string | FlagTypes;
  description: string;
  status: FlagStatus;
  createdAt: string;
  enrollmentId: string;
  enrollmentName: string;
  associatedUser: {
    _id: string;
    fullName: string;
  } | null;
};

interface UserFlagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserWithFlagsData | null;
  userType: UserRole.Mentor | UserRole.Student;
  onFlagUpdate?: () => void;
}

export function UserFlagsModal(props: UserFlagsModalProps) {
  const { isOpen, onClose, user, userType, onFlagUpdate } = props;
  const currentUser = useAuthState();
  const toast = useToast();
  const [flagToClose, setFlagToClose] = useState<FlagDetailsData | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [modalPage, setModalPage] = useState<DetailsModalPage>();
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string>();
  const [flagFilter, setFlagFilter] = useState<TriState>('indeterminate');

  const {
    data: userFlags,
    isLoading,
    refetch: refetchModalData,
  } = useGetUserFlagsDetails(
    user
      ? {
          userId: user._id,
          role: userType,
        }
      : undefined
  );

  const activeFlagsCount =
    userFlags?.flags?.filter((flag: FlagDetailsData) => Number(flag.status) === FlagStatus.ACTIVE)
      .length || 0;

  const closedFlagsCount =
    userFlags?.flags?.filter((flag: FlagDetailsData) => Number(flag.status) === FlagStatus.CLOSED)
      .length || 0;

  const isToggleInactive = activeFlagsCount === 0 || closedFlagsCount === 0;
  const autoFlagFilter =
    (activeFlagsCount === 0 && 'unchecked') ||
    (closedFlagsCount === 0 && 'checked') ||
    'indeterminate';

  useEffect(() => {
    if (!isLoading && userFlags?.flags?.length) {
      isToggleInactive && setFlagFilter(autoFlagFilter);
    }
  }, [isToggleInactive, autoFlagFilter, isLoading, userFlags]);

  const { data: userProfile } = useGetUserProfile(user?._id);

  const { mutate: markAsClosed } = useCloseMultipleFlags({
    onSuccess: () => {
      toast.success('Flag marked as closed');
      refetchModalData();
      onFlagUpdate?.();
      setFlagToClose(null);
    },
    onError: () => {
      toast.error("Couldn't mark flag as closed");
      setFlagToClose(null);
    },
  });

  const markAsClosedHandler = (flag: FlagDetailsData) => {
    setFlagToClose(flag);
  };

  const confirmMarkAsClosedHandler = () => {
    if (!flagToClose) return;

    markAsClosed({
      flagIds: [flagToClose._id],
      closedBy: currentUser?.userId,
    });
  };

  const userClickHandler = (userId: string) => {
    setModalPage(userType === UserRole.Mentor ? 'student' : 'mentor');
    setSelectedUserId(userId);
    setShowDetailsModal(true);
  };

  const enrollmentClickHandler = (enrollmentId: string) => {
    setSelectedEnrollmentId(enrollmentId);
    setShowEnrollmentModal(true);
  };

  const detailsModalCloseHandler = () => {
    setShowDetailsModal(false);
    setModalPage(undefined);
  };

  const enrollmentModalCloseHandler = () => {
    setShowEnrollmentModal(false);
    setSelectedEnrollmentId(undefined);
  };

  const flagFilterChangeHandler = (value: TriState) => {
    setFlagFilter(value);
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={7} className="h-64">
            <div className="w-full h-full flex justify-center items-center">
              <LoadingSpinner className="w-5 h-5" />
            </div>
          </td>
        </tr>
      );
    }

    if (!userFlags?.flags || userFlags.flags.length === 0) {
      return (
        <tr>
          <td colSpan={7}>
            <EmptyStateCard icon={<BlockIcon className="text-customGrey" />} text="No Data" />
          </td>
        </tr>
      );
    }

    const filteredFlags = userFlags.flags.filter((flag: FlagDetailsData) => {
      if (flagFilter === 'checked') {
        return Number(flag.status) === FlagStatus.ACTIVE;
      } else if (flagFilter === 'unchecked') {
        return Number(flag.status) === FlagStatus.CLOSED;
      }
      return true;
    });

    if (filteredFlags.length === 0) {
      return (
        <tr>
          <td colSpan={7}>
            <EmptyStateCard
              icon={<BlockIcon className="text-customGrey" />}
              text="No flags match the selected filter"
            />
          </td>
        </tr>
      );
    }

    return filteredFlags.map((flag: FlagDetailsData) => (
      <Table.Row key={flag._id}>
        <Table.Cell>{flag.description}</Table.Cell>

        <Table.Cell>
          <span
            className={mergeClassNames(
              'capitalize',
              Number(flag.status) === FlagStatus.ACTIVE ? 'text-customRedAccent' : 'text-green-500'
            )}
          >
            {FLAG_STATUS[Number(flag.status)]}
          </span>
        </Table.Cell>
        <Table.Cell>{new Date(flag.createdAt).toLocaleDateString()}</Table.Cell>
        <Table.Cell>
          {flag.associatedUser ? (
            <span
              className={mergeClassNames('text-teal-500 cursor-pointer hover:underline')}
              onClick={() => flag.associatedUser && userClickHandler(flag.associatedUser._id)}
            >
              {flag.associatedUser.fullName}
            </span>
          ) : (
            <span className="text-customGrey">N/A</span>
          )}
        </Table.Cell>
        <Table.Cell>
          <span
            className={mergeClassNames('text-teal-500 cursor-pointer hover:underline')}
            onClick={() => enrollmentClickHandler(flag.enrollmentId)}
          >
            {flag.enrollmentName}
          </span>
        </Table.Cell>
        <Table.Cell>
          {Number(flag.status) === FlagStatus.ACTIVE && (
            <div onClick={() => markAsClosedHandler(flag)} className="flex cursor-pointer gap-1">
              <img className="files-icon" src={FinishFlagIcon} alt="flag" />
              <span className="min-w-max text-customRedAccent">Mark as closed</span>
            </div>
          )}
        </Table.Cell>
      </Table.Row>
    ));
  };

  if (!user) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="large">
        <Modal.Body>
          <div className="flex justify-center items-center min-h-[200px]">
            <EmptyStateCard
              icon={<BlockIcon className="text-customGrey" />}
              text="No user data available"
            />
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="large">
        <Modal.Header title={`${userFlags?.total || 0} Flags for ${user.fullName}`}>
          {userType === UserRole.Mentor && userProfile?.mentorData?.status && (
            <div className="text-sm text-customGrey mb-1 w-full text-center font-bold">
              Status: {userProfile.mentorData.status}
            </div>
          )}
        </Modal.Header>

        <Modal.Body>
          {userType === UserRole.Student && (
            <StudentDetailsPageContext.Provider
              value={{
                student: userProfile,
                onEnrollmentModalOpen: enrollmentClickHandler,
                onSessionModalOpen: () => {},
                onMentorModalOpen: userClickHandler,
              }}
            >
              <StudentDetailsSection />
            </StudentDetailsPageContext.Provider>
          )}
          {userType === UserRole.Mentor && (
            <MentorDetailsPageContext.Provider
              value={{
                mentor: userProfile,
                onEnrollmentModalOpen: enrollmentClickHandler,
                onSessionModalOpen: () => {},
                onStudentModalOpen: userClickHandler,
              }}
            >
              <MentorDetailsSection showBio={false} />
            </MentorDetailsPageContext.Provider>
          )}
          <div className="my-4">
            <TriStateToggle
              title="Show Flags:"
              options={[
                { label: 'Active', value: 'checked' },
                { label: 'Closed', value: 'unchecked' },
                { label: 'All', value: 'indeterminate' },
              ]}
              value={flagFilter}
              onChange={flagFilterChangeHandler}
              isInactive={isToggleInactive}
            />
          </div>
          <Table>
            <Table.Header>
              <Table.Row isHeader>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell className="whitespace-nowrap">Created At</Table.HeaderCell>
                <Table.HeaderCell>
                  {userType === UserRole.Mentor ? 'Student' : 'Mentor'}
                </Table.HeaderCell>
                <Table.HeaderCell>Enrollment</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>{renderTableContent()}</Table.Body>
          </Table>
        </Modal.Body>
      </Modal>

      <Modal isOpen={!!flagToClose} onClose={() => setFlagToClose(null)} size="small">
        <Modal.Header title="Confirm Action" />
        <Modal.Body>
          <p className="text-sm text-customGrey mb-6">
            Are you sure you want to mark this flag as closed?
          </p>
          <div className="flex justify-end gap-4">
            <Button buttonText="Cancel" variant="secondary" onClick={() => setFlagToClose(null)} />
            <Button buttonText="Confirm" variant="primary" onClick={confirmMarkAsClosedHandler} />
          </div>
        </Modal.Body>
      </Modal>

      {showDetailsModal && (
        <DetailsModal
          page={modalPage}
          isOpen={true}
          onClose={detailsModalCloseHandler}
          {...(userType === UserRole.Mentor
            ? { studentId: selectedUserId }
            : { mentorId: selectedUserId })}
        />
      )}

      {showEnrollmentModal && (
        <DetailsModal
          page="enrollment"
          isOpen={true}
          onClose={enrollmentModalCloseHandler}
          enrollmentId={selectedEnrollmentId}
        />
      )}
    </>
  );
}
